import {
  BRIDGE_PROTOCOL_VERSION,
  type AssetAvailability,
  type AssetResolutionRequest,
  type CapabilityReport,
  type DesktopEvent,
  type IndexRequest,
  type LocalCapability,
  type LocalJobRef,
  LocalJobRequest,
  type MediaSession,
  type MediaSessionRequest,
  type RootDescriptor,
} from '@estudio/contracts';

/**
 * Puerto único para funciones locales. La UI nunca importa APIs de Tauri,
 * FFmpeg o Python: siempre pasa por aquí. B implementará `tauriBridge`.
 */
export interface DesktopBridge {
  getCapabilities(): Promise<CapabilityReport>;
  chooseRoots(): Promise<RootDescriptor[]>;
  requestIndex(input: IndexRequest): Promise<LocalJobRef>;
  resolveAsset(input: AssetResolutionRequest): Promise<AssetAvailability>;
  requestMediaSession(input: MediaSessionRequest): Promise<MediaSession>;
  enqueueLocalJob(input: LocalJobRequest): Promise<LocalJobRef>;
  cancelLocalJob(jobId: string): Promise<void>;
  subscribe(listener: (event: DesktopEvent) => void): () => void;
}

export class BridgeUnavailableError extends Error {
  readonly code = 'DEVICE_REQUIRED' as const;
  constructor(readonly operation: string) {
    super('Requiere aplicación de escritorio');
    this.name = 'BridgeUnavailableError';
  }
}

export const LOCAL_CAPABILITIES: LocalCapability[] = ['index', 'probe', 'transcribe', 'align', 'render', 'export_capcut'];

/**
 * Navegador: no hay motor local. Todo lo local se declara no disponible y la UI
 * ofrece importación manual. Nunca simula éxito.
 */
export function createBrowserBridge(): DesktopBridge {
  const report: CapabilityReport = {
    protocol_version: BRIDGE_PROTOCOL_VERSION,
    runtime: 'browser',
    engine: { available: false, version: null },
    capabilities: Object.fromEntries(LOCAL_CAPABILITIES.map((c) => [c, 'unsupported'])) as CapabilityReport['capabilities'],
    simulated: false,
  };
  const unavailable = (op: string) => () => Promise.reject(new BridgeUnavailableError(op));
  return {
    getCapabilities: () => Promise.resolve(report),
    chooseRoots: unavailable('chooseRoots'),
    requestIndex: unavailable('requestIndex'),
    resolveAsset: (input) =>
      Promise.resolve({
        asset_version_id: input.asset_version_id,
        state: 'unknown',
        checked_at: null,
        device_id: null,
        reason: 'La vista web no accede a archivos locales; abre la aplicación de escritorio.',
      }),
    requestMediaSession: unavailable('requestMediaSession'),
    enqueueLocalJob: unavailable('enqueueLocalJob'),
    cancelLocalJob: unavailable('cancelLocalJob'),
    subscribe: () => () => undefined,
  };
}

export interface MockBridgeOptions {
  /** Estado de disponibilidad por asset_version_id (fixtures). */
  availability?: Record<string, AssetAvailability['state']>;
  /** Tipos de trabajo que fallarán a mitad (para probar errores). */
  failTypes?: LocalJobRequest['type'][];
  stepMs?: number;
  roots?: RootDescriptor[];
  schedule?: (fn: () => void, ms: number) => void;
}

/**
 * Solo para demostración y pruebas. Todo lo que emite lleva simulated=true y
 * la UI lo muestra como «datos simulados». Nunca escribe en la cuenta real.
 */
export function createMockBridge(opts: MockBridgeOptions = {}): DesktopBridge {
  const listeners = new Set<(e: DesktopEvent) => void>();
  const cancelled = new Set<string>();
  let seq = 0;
  const schedule = opts.schedule ?? ((fn, ms) => void setTimeout(fn, ms));
  const step = opts.stepMs ?? 300;
  const emit = (e: DesktopEvent) => listeners.forEach((l) => l(e));
  const roots: RootDescriptor[] = opts.roots ?? [
    { id: '00000000-0000-4000-8000-00000000a001', label: 'Stock simulado', permissions: ['read'] },
  ];
  return {
    getCapabilities: () =>
      Promise.resolve({
        protocol_version: BRIDGE_PROTOCOL_VERSION,
        runtime: 'mock',
        engine: { available: true, version: '0.0.0-simulado' },
        capabilities: Object.fromEntries(LOCAL_CAPABILITIES.map((c) => [c, 'available'])) as CapabilityReport['capabilities'],
        simulated: true,
      }),
    chooseRoots: () => Promise.resolve(roots),
    requestIndex: (input) => {
      const job = crypto.randomUUID();
      [0, 1, 2].forEach((i) =>
        schedule(() => emit({ type: 'index_progress', seq: seq++, root_id: input.root_id, files_seen: (i + 1) * 10, files_indexed: i * 10, simulated: true }), step * (i + 1)),
      );
      return Promise.resolve({ job_id: job, local_ref: `mock-index-${job}`, simulated: true });
    },
    resolveAsset: (input) =>
      Promise.resolve({
        asset_version_id: input.asset_version_id,
        state: opts.availability?.[input.asset_version_id] ?? 'local_available',
        checked_at: new Date().toISOString(),
        device_id: null,
        reason: 'Simulado',
      }),
    requestMediaSession: (input) =>
      opts.availability?.[input.asset_version_id] === 'absent'
        ? Promise.reject(new Error('Archivo ausente (simulado)'))
        : Promise.resolve({
            session_id: `mock-${input.asset_version_id}`,
            url: 'about:blank#simulado',
            expires_at: new Date(Date.now() + 60_000).toISOString(),
            asset_version_id: input.asset_version_id,
          }),
    enqueueLocalJob: (input) => {
      if (!LocalJobRequest.safeParse(input).success) return Promise.reject(new Error('Solicitud de trabajo fuera de contrato'));
      const stages = ['preparar', 'procesar', 'verificar'];
      const fail = opts.failTypes?.includes(input.type);
      stages.forEach((stage, i) => {
        schedule(() => {
          if (cancelled.has(input.job_id)) return;
          if (fail && i === 1) {
            emit({ type: 'job_state', seq: seq++, job_id: input.job_id, attempt_generation: 1, state: 'failed', error: { code: 'SIMULATED_FAILURE', message: 'Fallo simulado' }, simulated: true });
            cancelled.add(input.job_id);
            return;
          }
          emit({ type: 'job_progress', seq: seq++, job_id: input.job_id, attempt_generation: 1, stage, stage_progress: 1, simulated: true });
          if (i === stages.length - 1) emit({ type: 'job_state', seq: seq++, job_id: input.job_id, attempt_generation: 1, state: 'completed', simulated: true });
        }, step * (i + 1));
      });
      return Promise.resolve({ job_id: input.job_id, local_ref: `mock-${input.job_id}`, simulated: true });
    },
    cancelLocalJob: (jobId) => {
      cancelled.add(jobId);
      emit({ type: 'job_state', seq: seq++, job_id: jobId, attempt_generation: 1, state: 'cancelled', simulated: true });
      return Promise.resolve();
    },
    subscribe: (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
  };
}
